import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/layout/PageHeader';
import { ReportControls, ReportView, type ReportControlsState } from '../../components/kyckConnect/ReportView';
import { Combobox } from '../../components/ui/Combobox';
import { Spinner } from '../../components/ui/Spinner';
import { clubsMatching, scopeOptions } from '../../lib/leads/clubScope';
import { LeadBotReportPage } from '../leadBot';
import { fetchLeadClubs } from '../speedToLead/api';
import { fetchStaffReport } from './api';

// THE STAFF KYCKCONNECT REPORTING PAGE (Phase 3, commit 6), in the staff look. Two tabs: "Trends" (the shared report, with Brand,
// Group and Club filters) and "Lead bot report" (the existing page, unchanged and still reachable: no metric is lost).
// ⚠️ The page decides nothing about the figures or who may see what: the server resolves the Brand / Group / Club choice and refuses an
// all-clients read to a non-admin (that message is shown as it arrives).

type Tab = 'trends' | 'bot';

function Trends() {
  const [controls, setControls] = useState<ReportControlsState>({ months: 6, grouping: 'week', compare: false });
  const [brandId, setBrandId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [accountId, setAccountId] = useState('');
  const clubs = useQuery({ queryKey: ['speed-to-lead', 'lead-clubs'], queryFn: fetchLeadClubs, staleTime: 5 * 60_000, retry: false });
  const all = clubs.data ?? [];
  const choice = { brandId: brandId || undefined, groupId: groupId || undefined };
  const clubOptions = clubsMatching(all, choice).map((c) => ({ value: c.id, label: c.name }));
  const report = useQuery({
    queryKey: ['kyckconnect', 'report', 'staff', brandId, groupId, accountId, controls],
    queryFn: () => fetchStaffReport({ accountId: accountId || null, brandId: brandId || null, groupId: groupId || null, ...controls }),
    retry: false,
  });
  return (
    <div className="space-y-4" data-testid="staff-report-trends">
      <div className="flex flex-wrap items-center gap-3">
        <Combobox value={brandId} onValueChange={(v) => { setBrandId(v); setAccountId(''); }} options={[{ value: '', label: 'All brands' }, ...scopeOptions(all, 'brand').map((o) => ({ value: o.id, label: o.name }))]} placeholder="All brands" aria-label="Brand" />
        <Combobox value={groupId} onValueChange={(v) => { setGroupId(v); setAccountId(''); }} options={[{ value: '', label: 'All groups' }, ...scopeOptions(all, 'group').map((o) => ({ value: o.id, label: o.name }))]} placeholder="All groups" aria-label="Group" />
        <Combobox value={accountId} onValueChange={setAccountId} options={[{ value: '', label: 'All clubs' }, ...clubOptions]} placeholder="All clubs" aria-label="Club" />
      </div>
      <ReportControls value={controls} onChange={setControls} />
      {report.isLoading ? (
        <Spinner />
      ) : report.isError || !report.data ? (
        <p className="text-muted-foreground" data-testid="staff-report-error">{report.error instanceof Error ? report.error.message : 'We could not load the report.'}</p>
      ) : (
        <ReportView report={report.data} />
      )}
    </div>
  );
}

export function KyckConnectReportsPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'bot' ? 'bot' : 'trends';
  const set = (t: Tab) => setParams(t === 'bot' ? { tab: 'bot' } : {}, { replace: true });
  return (
    <div>
      <PageHeader title="Reports" description="Trends across leads, calls and texts, and the lead bot report." />
      <div className="mb-4 flex gap-2" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'trends'} className={`rounded-md border px-3 py-1 ${tab === 'trends' ? 'bg-primary text-primary-foreground' : ''}`} onClick={() => set('trends')}>Trends</button>
        <button type="button" role="tab" aria-selected={tab === 'bot'} className={`rounded-md border px-3 py-1 ${tab === 'bot' ? 'bg-primary text-primary-foreground' : ''}`} onClick={() => set('bot')}>Lead bot report</button>
      </div>
      {tab === 'trends' ? <Trends /> : <LeadBotReportPage />}
    </div>
  );
}
